import { DailyEvolutionItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@babackapp/ui/components/chart";
import { useMemo } from "react";
import { Label, Pie, PieChart } from "recharts";
import useFormattedTimeRange from "../../home/lib/use-formatted-time-range";
import { waitingColor, shippingColor, deliveryColor } from "./colors";

type Props = {
  timeChartData: DailyEvolutionItemDto[];
};

export function ReturnStatusDistribution({ timeChartData }: Props) {
  const t = useTranslation();

  const pieChartConfig = {
    waiting: {
      label: t("operationsDashboard.status.waiting"),
      color: waitingColor,
    },
    shipping: {
      label: t("operationsDashboard.status.shipping"),
      color: shippingColor,
    },
    shipped: {
      label: t("operationsDashboard.status.shipped"),
      color: deliveryColor,
    },
  } satisfies ChartConfig;

  const formattedTimeRange = useFormattedTimeRange();
  const data = useMemo(
    () =>
      (timeChartData ?? []).reduce(
        (acc, entry) => {
          acc.shipped += entry.shipped.count;
          acc.waiting += entry.waiting.count;
          acc.shipping += entry.shipping.count;
          acc.total +=
            entry.shipped.count + entry.waiting.count + entry.shipping.count;
          return acc;
        },
        {
          shipped: 0,
          waiting: 0,
          shipping: 0,
          total: 0,
        },
      ),
    [timeChartData],
  );

  const chartData = [
    {
      type: t("operationsDashboard.status.waiting"),
      returns: data.waiting,
      fill: waitingColor,
    },
    {
      type: t("operationsDashboard.status.shipping"),
      returns: data.shipping,
      fill: shippingColor,
    },
    {
      type: t("operationsDashboard.status.shipped"),
      returns: data.shipped,
      fill: deliveryColor,
    },
  ];

  return (
    <Card className="col-span-1 flex flex-col w-full">
      <CardHeader className="pb-0">
        <CardTitle className="text-md">
          {t("operationsDashboard.returnStatus")}
        </CardTitle>
        <CardDescription>{formattedTimeRange}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 p-2 flex justify-center items-center">
        <ChartContainer
          config={pieChartConfig}
          className="h-[200px] w-[200px] max-w-full"
        >
          <PieChart width={200} height={200} className="h-full max-w-full">
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Pie
              data={chartData}
              dataKey="returns"
              nameKey="type"
              innerRadius="70%"
              outerRadius="100%"
              strokeWidth={5}
            >
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className="fill-foreground text-3xl font-bold"
                        >
                          {data.total}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 24}
                          className="fill-muted-foreground"
                        >
                          {t("operationsDashboard.repartitions.label")}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
          </PieChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        <div className="flex text-center items-center gap-2 leading-snug font-medium">
          {t("operationsDashboard.returnStatusDescription")}
        </div>
      </CardFooter>
    </Card>
  );
}
